import { css } from "../../../../styled-system/css";
import { formatMetricValue, type FormatOptions } from "../../../utils/formatNumber";
import { useLocalStorage } from "@reactuses/core";
import { useAppSelector } from "../../../redux/hooks";
import {
  isKnownMetric,
  metricNameSelector,
  metricToolTipSelector,
  type MetricConfig,
} from "../../../modules/merchandising/utils/metrics";
import Skeleton from "react-loading-skeleton";
import LargeValue from "./LargeValue";
import { useTranslation } from "react-i18next";

interface Props<T> {
  metric: MetricConfig<T>;
  data: T;
  center?: boolean;
  showTarget?: boolean;
  targetLabel?: string;
  isLoading?: boolean;
}

const DetailedMetric = <T,>({
  metric,
  data,
  center = false,
  showTarget = false,
  targetLabel,
  isLoading = false,
}: Props<T>) => {
  const [numberFormat] = useLocalStorage("numberFormat", "compact");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["metrics"]);

  // Extract values from metric config. Inline metrics whose id isn't in the
  // metrics registry skip the byId fallback — those selectors would miss and
  // surface the raw key string in the UI.
  const isRegistered = isKnownMetric(metric.id);
  const name =
    metric.name == null ?
      isRegistered ? t(metricNameSelector(metric.id))
      : metric.id
    : typeof metric.name === "string" ? metric.name
    : t(metric.name);
  const value = metric.accessor(data);
  const toolTip =
    metric.toolTip == null ?
      isRegistered ? t(metricToolTipSelector(metric.id))
      : ""
    : typeof metric.toolTip === "string" ? metric.toolTip
    : t(metric.toolTip);

  // Get compare value based on defaultCompare
  let compareValue: number | null = null;
  let compareType: "prev" | "delta" | "percent" | null = null;
  if ("defaultCompare" in metric && metric.defaultCompare) {
    compareType = metric.defaultCompare;
    const accessorKey = `${metric.defaultCompare}_accessor` as const;
    const accessor = metric[accessorKey];
    compareValue = accessor(data);
  }

  // Build format options from metric config
  const decimalPlaces = metric.decimalPlaces ?? (metric.format === "percent" ? 1 : 2);
  const formatOptions: FormatOptions = {
    format: metric.format as "number" | "percent" | "currency",
    decimalPlaces,
    compact: metric.compact,
    marketPlace,
  };

  const forceCompact =
    formatOptions.format === "percent" ? false : (formatOptions.compact ?? numberFormat !== "precise");
  const adjustedFormatOptions: FormatOptions =
    formatOptions.format === "percent" ? formatOptions : { ...formatOptions, compact: forceCompact };

  const displayValue = formatMetricValue(value, adjustedFormatOptions);

  // Format compare value based on its type
  let displayCompareValue: string | null = null;
  if (compareValue != null && compareType) {
    if (compareType === "prev") {
      // Use same format as main value
      displayCompareValue = formatMetricValue(compareValue, adjustedFormatOptions);
    } else if (compareType === "delta") {
      // Use delta format (number format with +/- sign)
      const adjustedDeltaValue = metric.format === "percent" && compareValue ? compareValue * 100 : compareValue;
      const deltaFormat: FormatOptions = {
        format: metric.format === "percent" ? "number" : (metric.format as "number" | "percent" | "currency"),
        decimalPlaces: metric.decimalPlaces ?? 1,
        compact: true,
        addPlus: true,
        marketPlace,
      };
      displayCompareValue = formatMetricValue(adjustedDeltaValue, deltaFormat);
    } else {
      // Display as percentage
      const percentFormat: FormatOptions = {
        format: "percent",
        decimalPlaces: 1,
        compact: false,
        addPlus: true,
        marketPlace,
      };
      displayCompareValue = formatMetricValue(compareValue, percentFormat);
    }
  }

  // Determine target color based on compareHighlight
  const compareHighlight = metric.compareHighlight ?? "positiveIsGood";

  // Use same color logic as MetricChangeRow
  const isIncrease = compareValue != null && compareValue > 0;
  const isDecrease = compareValue != null && compareValue < 0;

  const positive =
    (compareHighlight === "positiveIsGood" && isIncrease) || (compareHighlight === "negativeIsGood" && isDecrease);
  const negative =
    (compareHighlight === "positiveIsGood" && isDecrease) || (compareHighlight === "negativeIsGood" && isIncrease);

  const targetColor =
    positive ? "trellisPalette.12"
    : negative ? "trellisPalette.17"
    : "trellisPalette.2";

  const comparisonDisplay =
    showTarget && (displayCompareValue || isLoading) ?
      <>
        {isLoading ?
          <Skeleton width="60px" height="12px" />
        : <>
            {targetLabel && <>{targetLabel} </>}
            <span className={css({ color: targetColor, fontWeight: "bold" })}>{displayCompareValue}</span>
          </>
        }
      </>
    : undefined;

  return (
    <LargeValue
      name={name}
      value={displayValue}
      center={center}
      toolTip={toolTip}
      isLoading={isLoading}
      comparisonDisplay={comparisonDisplay}
    />
  );
};

export default DetailedMetric;
